<template lang="pug">
table.release_note
  thead(@click="showDetail()")
    th.type(v-bind:class="{'clickable': isMd || hasBr}")
      span(style="font-weight: bold; text-decoration: underline;") {{title}}
      span.icon(v-if='isMd || hasBr' v-bind:class="{'fa fa-caret-down': isCollapse, 'fa fa-caret-up': !isCollapse}")
      slot(name="header-extra" v-if="!isCollapse")
  tbody(v-if='isMd')
    tr
      td.content(v-html="marked(content)" v-bind:class="{'collapse': isCollapse}")
  tbody(v-if='!isMd && !hasBr')
    tr
      td.content {{ computedContent }}
  tbody(v-if='!isMd && hasBr')
    tr(v-for='($index, s) in computedContent' track-by="$index")
      td.content(v-bind:class="{'collapse': isCollapse}") {{s}}
</template>

<script>
import marked from 'marked';
import { HL_START, HL_END } from '../../mixins/searchMixin.js';

// Matches either sentinel token — used to strip tokens from URL attributes and to
// convert them into <span> wrappers in rendered text.
const TOKEN_RE = /[\uE000\uE001]/g;

function escapeHtml (str) {
  if (!str) return '';
  return str
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

// Reverse of escapeHtml. Used to normalize inputs that may arrive pre-escaped
// (e.g. marked pre-escapes href/text for GFM autolinks), so that escapeHtml
// afterwards produces exactly one layer of escaping.
function decodeHtml (str) {
  if (!str) return '';
  return str
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&');
}

function isSafeUrl (url) {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch (e) {
    return false;
  }
}

const renderer = new marked.Renderer();
renderer.link = function (href, title, text) {
  // Strip highlight tokens from URL/title — they are only meaningful in text nodes.
  const rawHref = decodeHtml(href).replace(TOKEN_RE, '');
  const safeHref = isSafeUrl(rawHref) ? escapeHtml(rawHref) : '#';
  const safeTitle = escapeHtml(decodeHtml(title || href).replace(TOKEN_RE, ''));
  return `<a href="${safeHref}" target="_blank" rel="noopener noreferrer" title="${safeTitle}" style="color:#337ab7;">${text} <i class="fa fa-external-link" style="font-size:11px;"></i></a>`;
};

// Convert highlight sentinel tokens into real <span> tags, but only in text nodes
// (content between > and <). Tokens that somehow land inside an attribute value are
// left alone to avoid breaking HTML structure.
function applyHighlightTokens (html) {
  if (html.indexOf(HL_START) === -1) return html;
  return html.replace(/>([^<]*)</g, (_, text) => {
    return '>' + text
      .replace(new RegExp(HL_START, 'g'), '<span style="background-color: yellow;">')
      .replace(new RegExp(HL_END, 'g'), '</span>') + '<';
  });
}

export default {
  props: {
    title: {
      type: String
    },
    content: {
      type: String
    },
    class: {
      type: String
    },
    isMd: {
      type: Boolean
    },
    defaultCollapse: {
      type: Boolean,
      default: true
    },
    expandAll: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      isCollapse: this.defaultCollapse
    }
  },
  computed: {
    uniContent () {
      return this.content.replace(/\r\n|\n\r|\r/, '\n')
    },
    hasBr () {
      return this.uniContent.indexOf('\n') !== -1
    },
    computedContent () {
      if (this.isMd || !this.hasBr) {
        return this.uniContent
      } else {
        return this.uniContent.split('\n')
      }
    }
  },
  methods: {
    marked (content) { return applyHighlightTokens(marked(content, { renderer })) },
    showDetail () {
      this.isCollapse = !this.isCollapse
    }
  },
  watch: {
    defaultCollapse(newVal) {
      this.isCollapse = newVal
    },
    expandAll(newVal) {
      this.isCollapse = !newVal
    }
  }
}
</script>

<style scoped lang="scss">
.type {
  color: #000;
  display: flex;
  align-items: center;
}
.icon {
  margin-left: 8px;
  transition: transform 0.3s ease;
  cursor: pointer; /* Add cursor pointer */
}
.icon.collapsed {
  transform: rotate(180deg);
}
.icon:hover {
  color: #007BFF; /* Change color on hover */
}
.content {
  padding-left: 0;
}
tr {
  display: table; // for firefox
}
.release_note {
  text-align: left;
  width: 100%;
  margin-bottom: 6px;
}
.collapse {
  display: none;
}
.clickable {
  cursor: pointer;
}
</style>



// WEBPACK FOOTER //
// LinedTable.vue?115ff93b